import React from "react";
import { Box, Button, Chip, Menu, MenuItem, Slide, Typography, useTheme } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useLocation, useNavigate } from "react-router-dom";
import { useWorkspaceRecents, type WorkspaceRecentItem } from "../contexts/WorkspaceRecentsContext";
import { useUnsavedNavigationGuardContext } from "../contexts/UnsavedNavigationGuardContext";

const TYPE_LABELS: Record<WorkspaceRecentItem["type"], string> = {
  object: "",
  risk: "Risk",
  mitigation: "Mitigation",
};

const OBJECT_TYPE_LABELS: Record<NonNullable<WorkspaceRecentItem["objectType"]>, string> = {
  ZONE: "Zone",
  CONDUIT: "Conduit",
  COMPONENT: "Component",
};

const TYPE_COLORS: Record<WorkspaceRecentItem["type"], string> = {
  object: "#B1CC0E",
  risk: "#F59E0B",
  mitigation: "#00B3D1",
};

const TYPE_TARGETS: Record<WorkspaceRecentItem["type"], { path: string; stateKey: string }> = {
  object: { path: "/workspace/objects", stateKey: "focusObjectId" },
  risk: { path: "/workspace/risks", stateKey: "focusRiskId" },
  mitigation: { path: "/workspace/mitigations", stateKey: "focusMitigationId" },
};

function formatVisitedAgo(viewedAt: number) {
  const diffMs = Math.max(0, Date.now() - viewedAt);
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) return `Visited ${Math.max(1, minutes)} min. ago.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Visited ${hours} hr. ago.`;
  const days = Math.floor(hours / 24);
  return `Visited ${days} day${days === 1 ? "" : "s"} ago.`;
}

export function WorkspaceRecentsMenu() {
  const { items } = useWorkspaceRecents();
  const navigate = useNavigate();
  const location = useLocation();
  const { guard } = useUnsavedNavigationGuardContext();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const ghostTextColor = isDark ? "rgba(255,255,255,0.2)" : "rgba(0, 0, 0, 0.26)";
  const ghostHoverColor = isDark ? "rgba(255,255,255,0.4)" : "rgba(0, 0, 0, 0.4)";
  const menuBg = theme.palette.background.paper;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const filteredItems = React.useMemo(() => {
    const path = location.pathname;
    const riskId = path.startsWith("/workspace/risks") ? Number(searchParams.get("riskId")) : null;
    const mitigationId = path.startsWith("/workspace/mitigations") ? Number(searchParams.get("mitigationId")) : null;
    const objectId = path.startsWith("/workspace/objects") ? Number(searchParams.get("objectId")) : null;

    return items.filter((item) => {
      if (item.type === "risk" && Number.isFinite(riskId ?? NaN)) {
        return item.id !== riskId;
      }
      if (item.type === "mitigation" && Number.isFinite(mitigationId ?? NaN)) {
        return item.id !== mitigationId;
      }
      if (item.type === "object" && Number.isFinite(objectId ?? NaN)) {
        return item.id !== objectId;
      }
      return true;
    });
  }, [items, location.pathname, searchParams]);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleSelect = (item: WorkspaceRecentItem) => {
    if (item.deleted) return;
    const target = TYPE_TARGETS[item.type];
    const perform = () => {
      navigate(target.path, { state: { [target.stateKey]: item.id } });
      closeMenu();
    };
    if (guard?.isDirty) {
      guard.requestNavigation(perform);
      return;
    }
    perform();
  };

  return (
    <>
      <Button
        onClick={openMenu}
        disableRipple
        sx={{
          color: ghostTextColor,
          fontWeight: 700,
          fontStyle: "italic",
          fontSize: theme.typography.h4.fontSize,
          textTransform: "none",
          letterSpacing: 1,
          lineHeight: 1,
          minWidth: 0,
          p: 0,
          "&:hover": { bgcolor: "transparent", color: ghostHoverColor },
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontStyle: "italic",
              fontSize: theme.typography.h4.fontSize,
              lineHeight: 1,
              color: "inherit",
            }}
          >
            Workspace
          </Typography>
          <Typography
            component="span"
            aria-hidden="true"
            sx={{
              color: "inherit",
              fontWeight: 400,
              lineHeight: 1,
              fontStyle: "normal",
              fontSize: theme.typography.h4.fontSize,
            }}
          >
            |
          </Typography>
          <HistoryIcon sx={{ fontSize: theme.typography.h4.fontSize, lineHeight: 1 }} />
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "left" }}
        transitionDuration={220}
        PaperProps={{
          sx: {
            width: "25vw",
            minWidth: 320,
            maxWidth: "25vw",
            height: "calc(100vh - 88px)",
            maxHeight: "calc(100vh - 88px)",
            mt: 0.5,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: menuBg,
            boxShadow: "0 18px 40px rgba(0,0,0,0.18)",
            position: "relative",
            transformOrigin: "top right",
            "&:before": {
              content: '""',
              position: "absolute",
              top: -6,
              right: 24,
              width: 12,
              height: 12,
              bgcolor: menuBg,
              transform: "rotate(45deg)",
              borderLeft: "1px solid",
              borderTop: "1px solid",
              borderColor: "divider",
            },
          },
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: 1 }}>
          <Box sx={{ px: 2, pb: 0.5 }}>
            <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 800 }}>
              Last seen
            </Typography>
          </Box>
          {filteredItems.length === 0 ? (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                No recent items yet
              </Typography>
            </MenuItem>
          ) : (
            filteredItems.map((item) => (
              <MenuItem
                key={`${item.type}-${item.id}`}
                onClick={() => handleSelect(item)}
                disabled={item.deleted}
                sx={{ alignItems: "stretch", px: 2, py: 0.5 }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "96px 1fr",
                    columnGap: 1,
                    alignItems: "start",
                    justifyItems: "start",
                    width: "100%",
                  }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      item.type === "object" && item.objectType
                        ? OBJECT_TYPE_LABELS[item.objectType]
                        : TYPE_LABELS[item.type]
                    }
                    sx={{
                      borderColor: TYPE_COLORS[item.type],
                      color: TYPE_COLORS[item.type],
                      fontWeight: 700,
                      mt: 0.25,
                      justifySelf: "start",
                    }}
                  />
                  <Box sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        pr: 1.5,
                        fontWeight: 700,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        textDecoration: item.deleted ? "line-through" : "none",
                        color: item.deleted ? "text.disabled" : "text.primary",
                      }}
                    >
                      {item.title || "Untitled"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: "block",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {formatVisitedAgo(item.viewedAt)}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))
          )}
        </Box>
      </Menu>
    </>
  );
}
